import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/base/PageHeader";
import { listingTemplates, templateChannelLabel } from "@/lib/listing-templates";
import {
  previewDatasets,
  datasetById,
  type PreviewDatasetId,
} from "@/lib/listing-template-datasets";
import TemplateRenderer from "../components/TemplateRenderer";
import { BrowserFrame, PhoneFrame } from "../components/PreviewFrame";

/**
 * Template review — a visual comparison surface for all six listing templates.
 * Each template renders the selected product profile on desktop and mobile so
 * the five designs can be judged side by side.
 */
export default function ListingTemplateReview() {
  const [datasetId, setDatasetId] = useState<PreviewDatasetId>("rich");
  const dataset = datasetById(datasetId);

  return (
    <div>
      <PageHeader
        title="Template review"
        subtitle="Compare all six templates with different product profiles"
        backTo="/listing-templates"
      />

      {/* Sticky review controls */}
      <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur border-b border-slate-200 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-3 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Product profile</span>
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-1 py-1">
              {previewDatasets.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDatasetId(d.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap ${
                    datasetId === d.id
                      ? "bg-slate-800 text-white"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1.5 lg:ml-auto flex-wrap">
            <span className="text-xs font-medium text-slate-500 mr-1 whitespace-nowrap">Jump to</span>
            {listingTemplates.map((t) => (
              <a
                key={t.id}
                href={`#${t.id}`}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-full hover:bg-slate-50 whitespace-nowrap"
              >
                {t.name}
              </a>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2.5">{dataset.description}</p>
      </div>

      {/* Template sections */}
      <div className="space-y-12">
        {listingTemplates.map((template) => (
          <section key={template.id} id={template.id} className="scroll-mt-28">
            {/* Section header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: template.accentSoft, color: template.accent }}
                >
                  <i className="ri-layout-masonry-line" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold text-slate-900">{template.name}</h2>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                      <i className="ri-store-2-line" />
                      {templateChannelLabel(template.channels)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{template.tagline}</p>
                </div>
              </div>
              <Link
                to={`/listing-templates/preview/${template.id}`}
                className="sm:ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 whitespace-nowrap"
              >
                <i className="ri-external-link-line" /> Open full preview
              </Link>
            </div>

            {/* Desktop + mobile panes */}
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-5 items-start">
              <BrowserFrame url="yourstore.example.com/product">
                <TemplateRenderer templateId={template.id} product={dataset.product} />
              </BrowserFrame>
              <div className="flex justify-center xl:justify-start">
                <PhoneFrame>
                  <TemplateRenderer templateId={template.id} product={dataset.product} />
                </PhoneFrame>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}